/**
 * Modal that shares a party's PDF/PNG ledger via the OS share sheet.
 *
 * Flow:
 *   1. On open, fetch the closing balance as-of `to` so the caption preview
 *      reflects what's actually at the bottom of the exported file.
 *   2. Show two action buttons ("Share as PDF" / "Share as PNG"). Picking one:
 *      - fetches the file from the existing /ledger.{pdf,png} endpoint
 *      - builds a File object with a caption derived from the closing balance
 *      - calls navigator.share({files:[file], text: caption})
 *   3. If the browser doesn't support file sharing (or share() throws), fall
 *      back to a normal download + a banner showing the caption with a
 *      Copy button so the user can paste it into WhatsApp manually.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import { Button, Spinner, ErrorBanner } from '../ui/primitives';
import { Modal } from '../ui/Modal';
import { buildShareCaption } from './shareCaption';
import { withCacheBust } from './exportUrl';

interface BalanceResponse {
  partyId: number;
  asOfDate: string | null;
  balance: string;
}

/** Mirror of the server-side slugify in src/http/routes/ledgerExport.ts. */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function filenameFor(partyId: number, partyName: string, from: string, to: string, format: 'pdf' | 'png'): string {
  const base = slugify(partyName) || `party-${partyId}`;
  return `ledger-${base}-${from}-${to}.${format}`;
}

export function ShareLedgerDialog({
  open,
  onClose,
  partyId,
  partyName,
  from,
  to,
  includeReversed,
}: {
  open: boolean;
  onClose: () => void;
  partyId: number;
  partyName: string;
  from: string;
  to: string;
  includeReversed: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fallback, setFallback] = useState<{ caption: string; copyState: 'idle' | 'copied' | 'failed' } | null>(null);

  // Fetch closing balance as-of `to`. The page header shows all-time balance;
  // we want the as-of-end-date value to match the PDF's bottom row.
  const balanceQ = useQuery<BalanceResponse>({
    queryKey: ['balance', partyId, to],
    queryFn: () => api.get<BalanceResponse>(`/api/parties/${partyId}/balance?asOf=${to}`),
    enabled: open && Number.isFinite(partyId),
  });

  const caption = balanceQ.data ? buildShareCaption(balanceQ.data.balance) : null;

  // ESC to close (unless a share is in flight).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  // Reset internal state when the dialog re-opens.
  useEffect(() => {
    if (open) {
      setError(null);
      setFallback(null);
    }
  }, [open]);

  if (!open) return null;

  async function shareLedger(format: 'pdf' | 'png'): Promise<void> {
    if (!caption) return;
    setBusy(true);
    setError(null);

    // ALWAYS copy the caption to the clipboard before sharing. WhatsApp (and
    // some other apps) drop the `text` field when files are attached via Web
    // Share — so the caption gets lost. Putting it on the clipboard means the
    // user can paste it manually after WhatsApp attaches the file.
    let clipboardCopied = false;
    try {
      await navigator.clipboard.writeText(caption);
      clipboardCopied = true;
    } catch {
      // Clipboard API can fail in non-secure contexts or older browsers —
      // not fatal; the fallback banner / preview still shows the caption.
    }

    try {
      const url = withCacheBust(`/api/parties/${partyId}/ledger.${format}?from=${from}&to=${to}&include_reversed=${includeReversed}`);
      const resp = await fetch(url, { credentials: 'include' });
      if (!resp.ok) {
        throw new Error(`Failed to fetch ${format.toUpperCase()} (${resp.status}).`);
      }
      const blob = await resp.blob();
      const filename = filenameFor(partyId, partyName, from, to, format);
      const file = new File([blob], filename, { type: blob.type });

      // Web Share API path.
      if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            text: caption,
            title: `Ledger — ${partyName}`,
          });
          // Success — close the dialog. Caption is already in clipboard so
          // the user can paste it if the target app dropped it.
          onClose();
          return;
        } catch (e) {
          // User cancelled the share sheet → leave the dialog open so they
          // can pick a different format or close manually.
          if (e instanceof Error && e.name === 'AbortError') {
            return;
          }
          // Any other share() failure → fall through to fallback path.
        }
      }

      // Fallback path: trigger a normal download + show caption banner.
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Give the browser a tick to start the download before revoking.
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      setFallback({ caption, copyState: clipboardCopied ? 'copied' : 'idle' });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function copyCaption(): Promise<void> {
    if (!fallback) return;
    try {
      await navigator.clipboard.writeText(fallback.caption);
      setFallback({ ...fallback, copyState: 'copied' });
    } catch {
      setFallback({ ...fallback, copyState: 'failed' });
    }
  }

  return (
    <Modal onClose={() => { if (!busy) onClose(); }} size="sm">
      <div className="p-5">
        <h2 id="share-ledger-title" className="text-base font-semibold text-ink">
          Share ledger — {partyName}
        </h2>
        <p className="mt-1 text-caption text-ink-muted">
          {from} to {to}
        </p>

        {/* Caption preview */}
        {fallback === null && (
          <div className="mt-4">
            <div className="text-caption font-semibold uppercase tracking-wide text-ink-muted">
              Caption
            </div>
            <div className="mt-1 rounded-md border border-border bg-surface-raised px-3 py-2 text-small">
              {balanceQ.isLoading || !caption ? (
                <span className="flex items-center gap-2 text-ink-muted">
                  <Spinner /> loading balance…
                </span>
              ) : (
                <span className="font-mono">{caption}</span>
              )}
            </div>
            <p className="mt-1.5 text-caption text-ink-muted">
              Copied to clipboard when you share — paste it in WhatsApp after the file attaches.
            </p>
          </div>
        )}

        {error && (
          <div className="mt-3">
            <ErrorBanner>{error}</ErrorBanner>
          </div>
        )}

        {/* Fallback banner (after download) */}
        {fallback !== null && (
          <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-3 text-small text-ink">
            <p className="text-caption text-amber-700">
              Your browser can&apos;t share files directly. The file was downloaded —
              paste this caption when you forward it:
            </p>
            <div className="mt-2 flex items-center justify-between gap-2 rounded border border-amber-200 bg-surface px-2 py-1.5">
              <span className="font-mono text-small">{fallback.caption}</span>
              <Button variant="ghost" onClick={copyCaption}>
                {fallback.copyState === 'copied' ? 'Copied' : fallback.copyState === 'failed' ? 'Select + ⌘C' : 'Copy'}
              </Button>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {fallback ? 'Close' : 'Cancel'}
          </Button>
          {fallback === null && (
            <>
              <Button
                variant="secondary"
                onClick={() => void shareLedger('png')}
                disabled={busy || balanceQ.isLoading || !caption}
              >
                {busy ? 'Sharing…' : 'Share as PNG'}
              </Button>
              <Button
                onClick={() => void shareLedger('pdf')}
                disabled={busy || balanceQ.isLoading || !caption}
              >
                {busy ? 'Sharing…' : 'Share as PDF'}
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
